// ragQuality.js — measure whether injected RAG context was actually USED (sprint
// verify-loop-observability E2).
//
// The Apolo harness CAPS how much context it injects (InjectionBudget) but never
// observes whether that context was RELEVANT. This computes two cheap, local, 0..1
// signals from the injected chunks + the final answer:
//   - retrievalUsedPct: share of injected chunks whose distinctive tokens show up in
//     the answer (did retrieval earn its budget?). This is the actionable one: ~0 with
//     a weak turn ⇒ retrieval/prompt is the weak stage.
//   - faithfulness: share of the answer's distinctive tokens that appear in ANY injected
//     chunk (is the answer grounded in what we retrieved?). A relative health proxy.
//
// PURE: no I/O, never throws. Heuristic (token overlap), local — an honest cheap signal,
// not a model-graded score. Consumer: the learning-loop weak-run signal + verify-gate
// localization. NOT a vanity metric.

const WORD_RE = /[a-z0-9]{4,}/gi;
// Common, low-signal words (PT+EN) that would inflate overlap without meaning.
const STOP = new Set([
  'this', 'that', 'with', 'from', 'have', 'their', 'there', 'these', 'those', 'about',
  'para', 'como', 'sobre', 'pelo', 'pela', 'quando', 'porque', 'tambem', 'também',
  'isso', 'esse', 'essa', 'pelos', 'pelas', 'sera', 'será', 'mais', 'cada',
]);

function tokenize(text) {
  const out = new Set();
  const m = String(text || '').toLowerCase().match(WORD_RE) || [];
  for (const w of m) if (!STOP.has(w)) out.add(w);
  return out;
}

// A chunk counts as "used" when ≥15% of its distinctive tokens appear in the answer —
// bounded so a long chunk needs proportional, not absolute, overlap.
function chunkUsed(chunkTokens, answerTokens) {
  if (chunkTokens.size === 0) return false;
  let hit = 0;
  for (const t of chunkTokens) if (answerTokens.has(t)) hit++;
  return hit / chunkTokens.size >= 0.15;
}

function round2(n) {
  return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}

export function scoreRagInjection({ injectedChunks = [], finalText = '' } = {}) {
  const chunks = Array.isArray(injectedChunks)
    ? injectedChunks.filter((c) => typeof c === 'string' && c.trim())
    : [];
  const total = chunks.length;
  if (total === 0) return { faithfulness: 0, retrievalUsedPct: 0, usedCount: 0, total: 0 };

  const answerTokens = tokenize(finalText);
  if (answerTokens.size === 0) return { faithfulness: 0, retrievalUsedPct: 0, usedCount: 0, total };

  const chunkTokenSets = chunks.map(tokenize);
  let used = 0;
  for (const ct of chunkTokenSets) if (chunkUsed(ct, answerTokens)) used++;

  const allChunkTokens = new Set();
  for (const ct of chunkTokenSets) for (const t of ct) allChunkTokens.add(t);
  let grounded = 0;
  for (const t of answerTokens) if (allChunkTokens.has(t)) grounded++;

  return {
    faithfulness: round2(grounded / answerTokens.size),
    retrievalUsedPct: round2(used / total),
    usedCount: used,
    total,
  };
}
